import { useContext, useState } from "react"
import { format } from "date-fns"
import { Field, Form, Formik } from "formik"
import { FiPaperclip, FiPlus, FiEye, FiTrash, FiEdit } from "react-icons/fi"
import { toast } from "react-toastify"
import { useDebouncedCallback } from "use-debounce"
import { useHistory } from "react-router-dom"
import classNames from "classnames"

import { Button, File, Select, Textbox } from "../../../../components/Form"
import Table from "../../../../components/Table"
import { api, authHeaders } from "../../../../services/api"
import formUtils from '../../../../util/form'
import { GlobalContext } from "../../../../contexts/GlobalContext"
import { concatFormacoes } from "../../EspacoMedico/DadosCadastrais"
import { openTabSafari } from "../../../../util/file"
import Modal from "../../../../components/Modal"
import Spinner from "../../../../components/Spinner"
import { FormInclusaoContainer } from './styles'
import { EditInPlace } from "../../../../components/EditInPlace"
import FormEndereco from "../../../../components/FormEndereco/v2"

export function DadosProjeto({ projeto, loadProjeto, id, tiposDocumento, funcoes, handleUpdateCampo }) {
	const { user, checkException } = useContext(GlobalContext)

	const history = useHistory()

	const [pessoasBusca, setPessoasBusca] = useState(null)
	const [nenhumaPessoaEncontrada, setNenhumaPessoaEncontrada] = useState(false)
	const [isSearchingPessoa, setIsSearchingPessoa] = useState(false)
	const [relatorioCargaPessoas, setRelatorioCargaPessoas] = useState(null)

	const condicao = projeto.pessoas.find(p => p.id === user.pessoa.id).condicao?.toLowerCase() || ''

	const eAutor = ['autor', 'co-autor'].includes(condicao)
	const eAvaliador = condicao === 'avaliador trl'

	const searchPerson = useDebouncedCallback(async function (text) {
		if (!text) {
			setPessoasBusca([])
			return
		}

		try {
			setIsSearchingPessoa(true)

			const response = await api.get('pessoa', {
				params: {
					nome: text
				},
				...authHeaders()
			})

			const resultado = response.filter(item => !projeto.pessoas.some(p => p.id === item.id))

			setNenhumaPessoaEncontrada(resultado.length === 0)

			setPessoasBusca(resultado.map(pessoa => ({
				id: pessoa.id,
				nome: pessoa.nome,
				formacao: pessoa.formacoes.reduce((result, item) => {
					return [
						...result,
						`${item.grau.descricao} em ${concatFormacoes(item.formacao)}`
					]
				}, []).join('<br/>')
			})))

			setIsSearchingPessoa(false)
		} catch (e) {
			checkException(e)
		}
	}, 1000)

	async function handleIncluirPessoa(values) {
		try {
			values = formUtils.extractFormValues(values)

			const { pessoa_id, pessoa_funcao } = values

			await api.patch(`projeto/${id}/add/${pessoa_id}`, { pessoa_funcao }, authHeaders())

			toast.success('Participante incluído.')

			loadProjeto()
		} catch (e) {
			checkException(e)
		}
	}

	function handleAbrirPessoa(pessoa) {
		history.push(`/perfil/${pessoa.id}`)
	}

	function handleAbrirDocumento(doc) {
		openTabSafari(doc.link)
	}

	async function handleRemoverPessoa(pessoaId) {
		try {
			await api.delete(`projeto/${id}/remove/${pessoaId}`, authHeaders())

			toast.success('Participante removido do projeto.')

			loadProjeto()

			return true
		} catch (e) {
			checkException(e)
		}

		return false
	}

	async function handleAddDocumento(values) {
		try {
			await api.post(`projeto/${id}/add_document`, {
				descricao: values.descricao,
				arquivo: values.arquivo
			}, authHeaders())

			toast.success('Documento adicionado ao projeto.')

			loadProjeto()
		} catch (e) {
			checkException(e)
		}
	}

	async function handleRemoverDocumento(id) {
		try {
			await api.delete(`documento/${id}`, authHeaders())

			toast.success('Documento removido do projeto.')

			return true
		} catch (e) {
			checkException(e)
		}

		return false
	}

	async function handleAddLocal(values) {
		try {
			values = formUtils.extractFormValues(values)

			await api.post(`projeto/${projeto.id}/local`, values, authHeaders())

			toast.success('Local incluído no projeto.')

			loadProjeto()
		} catch(e) {
			checkException(e)
		}
	}

	if (!projeto || !tiposDocumento || !funcoes) {
		return <Spinner />
	}

	return (
		<>
			<div className="animated fadeIn fast">
				<div className="flex flex-col justify-between lg:grid lg:gap-4 lg:grid-cols-[auto_280px]">
					<div className="flex flex-col mx-2 gap-x-8 gap-y-3 mt-6 w-full">
						<div className="flex gap-2">
							<span>Detalhes:</span>

							{eAutor ? (
								<EditInPlace
									name="descricao"
									onUpdate={handleUpdateCampo}
									originalValue={projeto.descricao}
									fieldType="Textarea"
								>
									{() => (
										<div className="flex gap-2 group">
											<span className="group-hover:border-b group-hover:border-dashed">
												{projeto.descricao}
											</span>

											<FiEdit size={16} className="hover:cursor-pointer mt-1 w-16" />
										</div>
									)}
								</EditInPlace>
							) : (
								<span className="group-hover:border-b group-hover:border-dashed">
									{projeto.descricao}
								</span>
							)}
						</div>

						<div className="flex gap-2">
							<span>Objetivo:</span>

							{eAutor ? (
								<EditInPlace
									name="objetivo"
									onUpdate={handleUpdateCampo}
									originalValue={projeto.objetivo}
									fieldType="Textarea"
								>
									{() => (
										<div className="flex gap-2 group">
											<span className="group-hover:border-b group-hover:border-dashed">
												{projeto.objetivo}
											</span>

											<FiEdit size={16} className="hover:cursor-pointer mt-1 w-16" />
										</div>
									)}
								</EditInPlace>
							) : (
								<span className="group-hover:border-b group-hover:border-dashed">
									{projeto.objetivo}
								</span>
							)}
						</div>

						<div className="flex gap-8">
							<div className="flex gap-2">
								<span>Início:</span>

								{eAutor ? (
									<EditInPlace
										name="inicio"
										onUpdate={handleUpdateCampo}
										originalValue={new Date(projeto.inicio)}
										fieldType="Calendar"
									>
										{() => (
											<div className="edit-in-place flex gap-2 group">
												<span className="group-hover:border-b group-hover:border-dashed">
													{format(new Date(projeto.inicio), 'dd/MM/yyyy')}
												</span>

												<FiEdit size={16} className="hover:cursor-pointer mt-1 w-6" />
											</div>
										)}
									</EditInPlace>
								) : (
									<span className="group-hover:border-b group-hover:border-dashed">
										{format(new Date(projeto.inicio), 'dd/MM/yyyy')}
									</span>
								)}
							</div>

							<div className="flex gap-2">
								<span>Fim:</span>

								{eAutor ? (
									<EditInPlace
										name="fim"
										onUpdate={handleUpdateCampo}
										originalValue={new Date(projeto.fim)}
										fieldType="Calendar"
									>
										{() => (
											<div className="edit-in-place flex gap-2 group">
												<span className="group-hover:border-b group-hover:border-dashed">
													{format(new Date(projeto.fim), 'dd/MM/yyyy')}
												</span>

												<FiEdit size={16} className="hover:cursor-pointer mt-1 w-6" />
											</div>
										)}
									</EditInPlace>
								) : (
									<span className="group-hover:border-b group-hover:border-dashed">
										{format(new Date(projeto.fim), 'dd/MM/yyyy')}
									</span>
								)}
							</div>
						</div>

						<div className="flex gap-2">
							<span>Área de atuação:</span>

							{eAutor ? (
								<EditInPlace
									name="area_atuacao"
									onUpdate={handleUpdateCampo}
									originalValue={projeto.area_atuacao}
									fieldType="Textarea"
								>
									{() => (
										<div className="flex gap-2 group">
											<span className="group-hover:border-b group-hover:border-dashed">
												{projeto.area_atuacao}
											</span>

											<FiEdit size={16} className="hover:cursor-pointer mt-1 w-6" />
										</div>
									)}
								</EditInPlace>
							) : (
								<span className="group-hover:border-b group-hover:border-dashed">
									{projeto.area_atuacao}
								</span>
							)}
						</div>
					</div>

					{!eAvaliador && (
						<span className="mt-4 flex gap-1 font-bold p-2 rounded-md bg-emerald-600 text-white w-fit h-fit lg:justify-self-end" style={{ fontSize: 16 }}>
							{`Percentual do MRL concluído: ${projeto.percentualConclusao || 0}%`}
						</span>
					)}
				</div>

				<div className={classNames(
					'flex flex-col lg:grid gap-6',
					{
						'lg:grid-cols-[2fr_1fr]': eAutor,
						'lg:grid-cols-1': !eAutor
					}
				)}>
					<Table
						caption="Participantes"
						headers={[
							{ name: 'nome', value: 'Nome' },
							{ name: 'condicao', value: 'Função', centered: true },
							{ name: 'formacao', value: 'Formações' }
						]}
						data={projeto.pessoas.map(pessoa => {
							const aceiteDescricao = pessoa.aceite === true
								? pessoa.nome
								: pessoa.aceite === null
									? `${pessoa.nome}<br/><span class="text-sky-700" style="font-size: 11px">Aceitação pendente</span>`
									: `${pessoa.nome}<br/><span style="color: red; font-size: 11px">Aceite negado</span>`

							return {
								...pessoa,
								nome: aceiteDescricao
							}
						})}
						actions={[
							{
								action: handleAbrirPessoa,
								name: 'Ver perfil',
								icon: FiEye,
								iconSize: 16
							},
							projeto.autor.id === user.pessoa.id && {
								action: item => {
									handleRemoverPessoa(item.id)
								},
								name: 'Remover',
								icon: FiTrash,
								iconSize: 16,
								confirmation: 'Deseja realmente remover este participante do projeto?',
								checkDisabled: item => item.condicao === 'Autor' || item.id === user.pessoa.id
							}
						]}
						confirmExclusion={{
							template: 'Deseja realmente remover <b>#attr#</b> do projeto?',
							attr: 'nome'
						}}
					/>

					{eAutor ? (
						<FormInclusaoContainer>
							<details className="peer open:border open:border-white rounded-md group">
								<summary className="px-2 border border-white rounded-md py-2 px-3 cursor-pointer hover:bg-[#fff1] list-none group-open:border-0 group-open:font-semibold">
									Incluir participantes e avaliadores
								</summary>

								<div className="px-4 pb-3 flex flex-col">
									<Formik
										onSubmit={handleIncluirPessoa}
										initialValues={{
											pessoa_id: null,
											pessoa_funcao: null
										}}
									>
										{({ setFieldValue }) => (
											<Form className="flex flex-col items-end">
												<Textbox
													name="pessoa_busca"
													label="Busque por nome"
													onChange={e => {
														setFieldValue('pessoa_busca', e.target.value)

														searchPerson(e.target.value)
													}}
												/>

												{isSearchingPessoa ? (
													<div className="italic text-sm mx-2">Buscando...</div>
												) : !!pessoasBusca?.length ? (
													<div className="w-full">
														<ul className="mx-2">
															{pessoasBusca.map(pessoa => (
																<li
																	key={pessoa.id}
																	className="bg-white text-slate-800 hover:bg-slate-200 p-2 first:rounded-t-md last:rounded-b-md cursor-pointer"
																	onClick={() => {
																		setFieldValue('pessoa_id', pessoa.id)

																		setPessoasBusca(null)
																		setFieldValue('pessoa_busca', pessoa.nome)
																	}}
																>
																	{pessoa.nome}
																</li>
															))}
														</ul>
													</div>
												) : nenhumaPessoaEncontrada ? (
													<div className='italic mx-2 text-xs mt-2 text-center w-full mb-3'>Nenhuma pessoa correspondente à busca</div>
												) : null}

												<Select
													name="pessoa_funcao"
													label="Função"
													onChange={(selected, meta) => {
														setFieldValue(meta.name, selected)
													}}
													options={funcoes.map(funcao => ({
														label: funcao.descricao,
														value: funcao.id
													}))}
												/>

												<Button type="submit" className="transparent self-end">
													Incluir pessoa
													<FiPlus size={16} />
												</Button>

												<Field type="hidden" name="pessoa_id" />
											</Form>
										)}
									</Formik>
								</div>
							</details>
						</FormInclusaoContainer>
					) : <div></div>}

					<Table
						caption="Empresas investidoras"
						headers={[
							{ name: 'razao_social', value: 'Nome' },
							{ name: 'cnpj', value: 'CNPJ', centered: true }
						]}
						data={projeto.empresas}
						emptyLabel="Nenhuma empresa incluída neste projeto"
					/>
				</div>

				{eAutor ? (
					<>
						<hr className="border-[#fff5] mt-4 lg:col-span-2" />

						<div className={classNames(
							'flex flex-col lg:grid gap-6',
							{
								'lg:grid-cols-[2fr_1fr]': eAutor,
								'lg:grid-cols-1': !eAutor
							}
						)}>
							<Table
								caption="Locais"
								headers={[
									{ name: 'nome', value: 'Local' },
									{ name: 'endereco_string', value: 'Endereço' }
								]}
								data={projeto.locais}
							/>

							<details className="peer open:border open:border-white rounded-md group mt-1 lg:mt-14">
								<summary className="px-2 border border-white rounded-md py-2 px-3 cursor-pointer hover:bg-[#fff1] list-none group-open:border-0 group-open:font-semibold">
									Incluir locais
								</summary>

								<div className="px-4 pb-3 flex flex-col">
									<Formik
										onSubmit={handleAddLocal}
										initialValues={{
											nome: '',
											cep: '',
											uf: '',
											cidade: '',
											bairro: '',
											logradouro: '',
											numero: '',
											complemento: ''
										}}
									>
										{({ setFieldValue }) => (
											<Form className="flex flex-col">
												<Textbox name="nome" label="Nome do local" />

												<FormEndereco 
													setFieldValue={setFieldValue}
												/>

												<Button type="submit" className="transparent w-fit self-end">
													Incluir local
													<FiPlus size={16} />
												</Button>
											</Form>
										)}
									</Formik>
								</div>
							</details>
						</div>

						<hr className="border-[#fff5] mt-4 lg:col-span-2" />

						<h2 className="font-semibold px-2 pt-4" style={{ fontSize: 16 }}>Documentos do projeto</h2>

						<Formik
							onSubmit={handleAddDocumento}
							initialValues={{
								arquivo: null,
								descricao: null
							}}
						>
							{({ setFieldValue, errors }) => (
								<Form className="flex flex-col lg:grid lg:grid-cols-[1fr_1fr_1.5fr] lg:gap-x-4 lg:col-span-2">
									<File
										name="arquivo"
										label="Anexe o documento"
										onSuccess={fileId => setFieldValue('arquivo', fileId)}
										format="square"
										error={errors.arquivo}
										style={{ width: 'calc(100% - 16px)' }}
										previewSize={['100%', '140px']}
									/>

									<div className="flex flex-col justify-between">
										<Textbox
											name="descricao"
											label="Nome do documento"
										/>

										<Button type="submit" className="transparent w-fit self-end lg:-translate-y-6">
											Anexar documento
											<FiPaperclip size={16} />
										</Button>
									</div>

									<Table
										headers={[
											{ name: 'descricao', value: 'Documento' }
										]}
										data={projeto.documentos}
										actions={[
											{
												action: handleAbrirDocumento,
												icon: FiEye,
												iconSize: 16,
												name: 'Abrir'
											}
										]}
										handleDelete={handleRemoverDocumento}
										confirmExclusion={{
											template: 'Deseja realmente remover o documento <b>#attr#</b> do projeto?',
											attr: 'tipo'
										}}
										style={{
											marginTop: 28
										}}
									/>
								</Form>
							)}
						</Formik>
					</>
				) : (
					<div></div>
				)}
			</div>

			<Modal
				isOpen={!!relatorioCargaPessoas}
				handleClose={() => {
					setRelatorioCargaPessoas(null)
				}}
				title="Relatório da carga de participantes"
			>
				{relatorioCargaPessoas && (
					<div className="mx-2">
						<p className="mt-4" style={{ fontSize: 17 }}>Pessoas vinculadas ao projeto: {relatorioCargaPessoas.pessoas_vinculadas}</p>

						<h2 className="font-semibold mt-4 border-t border-[#fff5] pt-3" style={{ fontSize: 18 }}>Inconsistências</h2>

						<Table
							caption="CPFs inválidos"
							headers={[
								{ name: 'linha', value: 'Número da linha', style: { width: 160 } },
								{ name: 'Nome', value: 'Nome' },
								{ name: 'CPF', value: 'CPF', centered: true, style: { width: 140 } }
							]}
							data={relatorioCargaPessoas.cpf_invalido}
						/>

						<Table
							caption="Pessoas sem nome"
							headers={[
								{ name: 'linha', value: 'Número da linha', style: { width: 160 } },
								{ name: 'Nome', value: 'Nome' },
								{ name: 'CPF', value: 'CPF', centered: true, style: { width: 140 } }
							]}
							data={relatorioCargaPessoas.sem_nome}
						/>

						<Table
							caption="Grau de formação não especificado"
							headers={[
								{ name: 'linha', value: 'Número da linha', style: { width: 160 } },
								{ name: 'Nome', value: 'Nome' },
								{ name: 'CPF', value: 'CPF', centered: true, style: { width: 140 } },
								{ name: 'Grau', value: 'Grau', centered: true },
								{ name: 'Formação', value: 'Formação', centered: true }
							]}
							data={relatorioCargaPessoas.grau_incorreto}
						/>

						<Table
							caption="Formação não especificada"
							headers={[
								{ name: 'linha', value: 'Número da linha', style: { width: 160 } },
								{ name: 'Nome', value: 'Nome' },
								{ name: 'CPF', value: 'CPF', centered: true, style: { width: 140 } },
								{ name: 'Grau', value: 'Grau', centered: true },
								{ name: 'Formação', value: 'Formação', centered: true }
							]}
							data={relatorioCargaPessoas.formacao_incorreta}
						/>
					</div>
				)}
			</Modal>
		</>
	)
}